import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
  Alert,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Pix as PixIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';

import { StoreFormData } from './types/store.types';

const PaymentConfigForm: React.FC = () => {
  const { register, watch, setValue, formState: { errors } } = useFormContext<StoreFormData>();
  
  const [paymentMethods, setPaymentMethods] = useState<string[]>(['credit_card', 'pix']);
  const [allowInstallments, setAllowInstallments] = useState(false);
  const [maxInstallments, setMaxInstallments] = useState(12);

  const watchedPaymentMethods = watch('paymentMethods') || [];
  const watchedAllowInstallments = watch('allowInstallments') || false;
  const watchedMaxInstallments = watch('maxInstallments') || 12;
  const watchedMinimumOrderValue = watch('minimumOrderValue') || 0;
  const watchedFreeShippingValue = watch('freeShippingValue') || 100;

  // Opções de métodos de pagamento
  const paymentOptions = [
    {
      value: 'credit_card',
      label: 'Cartão de Crédito',
      icon: <CreditCardIcon />,
      description: 'Aceita Visa, Mastercard, Elo, etc.',
      popular: true,
    },
    {
      value: 'debit_card',
      label: 'Cartão de Débito',
      icon: <CreditCardIcon />,
      description: 'Aceita cartões de débito',
      popular: true,
    },
    {
      value: 'pix',
      label: 'PIX',
      icon: <PixIcon />,
      description: 'Pagamento instantâneo via PIX',
      popular: true,
    },
    {
      value: 'boleto',
      label: 'Boleto Bancário',
      icon: <BankIcon />,
      description: 'Pagamento via boleto bancário',
      popular: false,
    },
    {
      value: 'paypal',
      label: 'PayPal',
      icon: <PaymentIcon />,
      description: 'Pagamento via PayPal',
      popular: false,
    },
    {
      value: 'mercado_pago',
      label: 'Mercado Pago',
      icon: <PaymentIcon />,
      description: 'Pagamento via Mercado Pago',
      popular: true,
    },
  ];

  const handlePaymentMethodChange = (method: string, checked: boolean) => {
    let newMethods = [...watchedPaymentMethods];
    
    if (checked) {
      newMethods.push(method);
    } else {
      newMethods = newMethods.filter(m => m !== method);
    }
    
    setPaymentMethods(newMethods);
    setValue('paymentMethods', newMethods);
  };

  const handleInstallmentsChange = (checked: boolean) => {
    setAllowInstallments(checked);
    setValue('allowInstallments', checked);
    
    if (!checked) {
      setValue('maxInstallments', 1);
    }
  };

  const handleMaxInstallmentsChange = (value: number) => {
    setMaxInstallments(value);
    setValue('maxInstallments', value);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentIcon color="primary" />
        Configurações de Pagamento
      </Typography>

      {/* Métodos de Pagamento */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Métodos de Pagamento Aceitos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Selecione quais métodos de pagamento sua loja aceitará
          </Typography>

          <FormControl component="fieldset" error={!!errors.paymentMethods}>
            <FormGroup>
              <Grid container spacing={2}>
                {paymentOptions.map((option) => (
                  <Grid item xs={12} md={6} key={option.value}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        border: watchedPaymentMethods.includes(option.value) ? 2 : 1,
                        borderColor: watchedPaymentMethods.includes(option.value) ? 'primary.main' : 'grey.300',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => handlePaymentMethodChange(option.value, !watchedPaymentMethods.includes(option.value))}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Checkbox
                            checked={watchedPaymentMethods.includes(option.value)}
                            onChange={(e) => handlePaymentMethodChange(option.value, e.target.checked)}
                            color="primary"
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                            {option.icon}
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                  {option.label}
                                </Typography>
                                {option.popular && (
                                  <Chip label="Popular" size="small" color="primary" variant="outlined" />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
            {errors.paymentMethods && (
              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 1.5 }}>
                {errors.paymentMethods.message}
              </Typography>
            )}
          </FormControl>
        </CardContent>
      </Card>

      {/* Configurações de Parcelamento */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Parcelamento
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={watchedAllowInstallments}
                onChange={(e) => handleInstallmentsChange(e.target.checked)}
              />
            }
            label="Permitir parcelamento"
          />
          
          {watchedAllowInstallments && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Máximo de parcelas"
                type="number"
                value={watchedMaxInstallments}
                onChange={(e) => handleMaxInstallmentsChange(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 24 }}
                helperText="Número máximo de parcelas permitidas (1 a 24)"
                sx={{ maxWidth: 200 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Configurações de Frete */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShippingIcon color="primary" />
            Configurações de Frete
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valor mínimo do pedido"
                type="number"
                value={watchedMinimumOrderValue}
                onChange={(e) => setValue('minimumOrderValue', parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
                }}
                helperText="Valor mínimo para permitir o pedido"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valor para frete grátis"
                type="number"
                value={watchedFreeShippingValue}
                onChange={(e) => setValue('freeShippingValue', parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
                }}
                helperText="Valor do pedido para ganhar frete grátis"
              />
            </Grid>
          </Grid>
          
          {watchedFreeShippingValue > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Pedidos acima de R$ {watchedFreeShippingValue.toFixed(2)} terão frete grátis
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Resumo das Configurações */}
      <Card sx={{ bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resumo das Configurações
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Métodos de Pagamento:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {watchedPaymentMethods.map((method) => {
                  const option = paymentOptions.find(opt => opt.value === method);
                  return (
                    <Chip
                      key={method}
                      label={option?.label || method}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Parcelamento:
              </Typography>
              <Typography variant="body2">
                {watchedAllowInstallments 
                  ? `Até ${watchedMaxInstallments} parcelas`
                  : 'Não permitido'
                }
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Valor Mínimo:
              </Typography>
              <Typography variant="body2">
                R$ {watchedMinimumOrderValue.toFixed(2)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Frete Grátis:
              </Typography>
              <Typography variant="body2">
                {watchedFreeShippingValue > 0 
                  ? `Acima de R$ ${watchedFreeShippingValue.toFixed(2)}`
                  : 'Não configurado'
                }
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dicas */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          💡 Dicas para configuração de pagamento:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • PIX é o método mais rápido e com menor taxa<br />
          • Cartão de crédito aumenta conversão de vendas<br />
          • Parcelamento sem juros atrai mais clientes<br />
          • Configure valores mínimos para evitar pedidos muito pequenos<br />
          • Frete grátis é um grande incentivador de compra
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentConfigForm;
